import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleMark } from "@/components/GoogleMark";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const detail = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);
const backHere = (code) => sessionStorage.setItem("cj_next", `/join/${code}`);

export const JoinHeader = ({ info }) => (
  <div className="px-6 pt-8 pb-7 text-white" style={{ background: info.color }} data-testid="join-header">
    {info.logo_url
      ? <img src={info.logo_url} alt={info.company_name} className="max-h-14 max-w-[220px] rounded-md bg-white p-1.5" />
      : <p className="font-display text-2xl font-extrabold">{info.company_name}</p>}
    <h1 className="font-display mt-5 text-3xl font-extrabold leading-tight" data-testid="join-title">Join the {info.company_name} crew</h1>
    <p className="mt-1.5 text-white/85">{info.depot_name ? `${info.depot_name} · ` : ""}Your jobs, shifts and timesheets in one app.</p>
  </div>
);

const googleLogin = (code) => {
  backHere(code);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const JoinForm = ({ code, onDone }) => {
  const { register } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await register(f, (p) => api.joinCrew(code, p)); onDone(f.email.trim().toLowerCase()); }
    catch (err) { toast.error(detail(err, "Could not create your login — please try again")); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="join-form">
      <div className="space-y-1.5"><Label htmlFor="j-name">Your name</Label>
        <Input id="j-name" required autoComplete="name" value={f.name} onChange={set("name")} placeholder="First and last name" data-testid="join-name" /></div>
      <div className="space-y-1.5"><Label htmlFor="j-email">Email</Label>
        <Input id="j-email" type="email" required autoComplete="email" autoCapitalize="none" value={f.email} onChange={set("email")} placeholder="you@email.com" data-testid="join-email" /></div>
      <div className="space-y-1.5"><Label htmlFor="j-pass">Make a password</Label>
        <div className="relative">
          <Input id="j-pass" type={show ? "text" : "password"} required minLength={6} autoComplete="new-password" value={f.password} onChange={set("password")} placeholder="At least 6 characters" className="pr-10" data-testid="join-password" />
          <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground" data-testid="join-password-toggle">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div></div>
      <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white text-base" data-testid="join-submit">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}Join the crew
      </Button>
      <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" /></div>
      <Button type="button" variant="outline" onClick={() => googleLogin(code)} className="w-full h-12 rounded-full" data-testid="join-google"><GoogleMark />Continue with Google</Button>
      <p className="text-sm text-center text-muted-foreground">Already have a Cross Jobs login?{" "}
        <Link to="/login" onClick={() => backHere(code)} className="font-semibold text-primary hover:underline" data-testid="join-signin-link">Sign in to join</Link></p>
    </form>
  );
};
